import type React from "react";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";
import { Button } from "@planta-v2/pds-ui/button";
import { BackButton } from "@planta-v2/pds-ui/back-button";
import {
  UnderlineTabs,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  UnderlineTabsContent,
} from "@planta-v2/pds-ui/underline-tabs";
import { FeatureNotImplementedModal } from "@frontend/shared/presentation/components/feedback/FeatureNotImplementedModal";
import { AlertCircle, RefreshCw, X } from "lucide-react";
import { i18n } from "@frontend/i18n";
import { useEventTheme } from "@/shared/presentation/hooks/useEventTheme";

import {
  useProfileDetailController,
  ProfileTab,
} from "../controllers/useProfileDetailController";
import { ProfileHeader } from "@frontend/shared/presentation/components/participant-profile/ProfileHeader";
import { ProfileTabContent } from "@frontend/shared/presentation/components/participant-profile/ProfileTabContent";
import { WhyConnectTabContent } from "../components/profile/WhyConnectTabContent";
import { RequestIntroModal } from "../components/profile/RequestIntroModal";
import { CTAContent } from "../components/connection/ProfileCTA";

function ProfileDetailSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 px-4">
      <div className="flex gap-4">
        <Skeleton className="h-20 w-20 rounded-full flex-shrink-0" />
        <div className="flex flex-col gap-2 flex-1">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-5 w-48" />
        </div>
      </div>
      <Skeleton className="h-8 w-full" />
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}

interface ErrorStateProps {
  readonly onRetry: () => void;
}

function ErrorState({ onRetry }: ErrorStateProps): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12 px-4">
      <AlertCircle className="h-12 w-12 text-destructive/50" />
      <div className="text-center">
        <h2 className="mb-2.5 font-serif text-base font-normal text-foreground">
          {i18n.t("user.directory.profile.error.title")}
        </h2>
        <p className="text-pds-2xs leading-relaxed text-muted-foreground">
          {i18n.t("user.directory.profile.error.description")}
        </p>
      </div>
      <Button onClick={onRetry} variant="outline" className="gap-2">
        <RefreshCw className="h-4 w-4" />
        {i18n.t("user.directory.profile.error.retry")}
      </Button>
    </div>
  );
}

function NotFoundState(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12 px-4">
      <AlertCircle className="h-12 w-12 text-muted-foreground" />
      <div className="text-center">
        <h2 className="mb-2.5 font-serif text-base font-normal text-foreground">
          {i18n.t("user.directory.profile.notFound.title")}
        </h2>
        <p className="text-pds-2xs leading-relaxed text-muted-foreground">
          {i18n.t("user.directory.profile.notFound.description")}
        </p>
      </div>
    </div>
  );
}

export function ProfileDetailPage(): React.JSX.Element {
  const {
    state,
    actions,
    ctaProps,
    suggestions,
    isSuggestionsLoading,
    showWhyConnect,
    isOwnProfile,
  } = useProfileDetailController();

  useEventTheme(state.themeMode);

  const {
    profile,
    isLoading,
    isError,
    error,
    isNotFound,
    activeTab,
    isRequestIntroModalOpen,
    isRequestingIntro,
    mutationErrorMessage,
    isFeatureNotImplementedModalOpen,
  } = state;
  const {
    handleBack,
    setActiveTab,
    handleRetry,
    closeRequestIntroModal,
    handleSubmitIntroRequest,
    clearMutationError,
    closeFeatureNotImplementedModal,
    handleEditProfile,
  } = actions;

  const handleTabChange = (value: string): void => {
    if (value === ProfileTab.PROFILE || value === ProfileTab.WHY_CONNECT) {
      setActiveTab(value);
    }
  };

  // The loaded branch keeps its own copy inside UnderlineTabs, which relies on
  // h-full; hoisting the row above the tabs would break that. The other three
  // states have no way back at all without this — NotFoundState in particular
  // offers nothing, not even a retry.
  const backRow = (
    <div className="flex-shrink-0 px-4 py-4">
      <BackButton
        onClick={handleBack}
        data-testid="profile-back-button"
        label={i18n.t("user.directory.profile.back")}
      />
    </div>
  );

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Loading/Error/NotFound States */}
      {isLoading && (
        <>
          {backRow}
          <div className="flex-1 overflow-y-auto">
            <ProfileDetailSkeleton />
          </div>
        </>
      )}

      {isError && error !== null && (
        <>
          {backRow}
          <div className="flex-1 overflow-y-auto">
            <ErrorState onRetry={handleRetry} />
          </div>
        </>
      )}

      {isNotFound && (
        <>
          {backRow}
          <div className="flex-1 overflow-y-auto">
            <NotFoundState />
          </div>
        </>
      )}

      {/* Profile Content - when loaded */}
      {profile !== undefined && !isLoading && !isError && (
        <UnderlineTabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="flex h-full flex-col"
        >
          {/* Fixed Header Section */}
          <div className="flex-shrink-0 bg-background">
            {/* Back Button Row */}
            <div className="px-4 py-4">
              <BackButton
                onClick={handleBack}
                data-testid="profile-back-button"
                label={i18n.t("user.directory.profile.back")}
              />
            </div>

            {/* Profile Header and Tabs Section */}
            <div className="px-4 pb-4">
              <ProfileHeader
                displayName={profile.entry.displayName}
                photoUrl={profile.entry.photoUrl}
                headline={profile.entry.headline}
                company={profile.entry.company}
              />
            </div>

            <UnderlineTabsList className="px-4" testId="profile-tabs-list">
              <UnderlineTabsTrigger value={ProfileTab.PROFILE}>
                {i18n.t("user.directory.profile.tabs.profile")}
              </UnderlineTabsTrigger>
              {showWhyConnect && (
                <UnderlineTabsTrigger value={ProfileTab.WHY_CONNECT}>
                  {i18n.t("user.directory.profile.tabs.whyConnect")}
                </UnderlineTabsTrigger>
              )}
            </UnderlineTabsList>
          </div>

          {/* Scrollable Tab Content */}
          <UnderlineTabsContent
            value={ProfileTab.PROFILE}
            className="flex-1 overflow-y-auto min-h-0 px-4 pb-32"
          >
            <ProfileTabContent
              entry={profile.entry}
              isOwnProfile={isOwnProfile}
            />
          </UnderlineTabsContent>

          {showWhyConnect && (
            <UnderlineTabsContent
              value={ProfileTab.WHY_CONNECT}
              className="flex-1 overflow-y-auto min-h-0 px-4 pb-32"
            >
              <WhyConnectTabContent
                suggestions={suggestions}
                isLoading={isSuggestionsLoading}
              />
            </UnderlineTabsContent>
          )}
        </UnderlineTabs>
      )}

      {/* Edit Profile Button - own profile */}
      {isOwnProfile && profile !== undefined && (
        <div className="fixed inset-x-0 bottom-0 border-t border-border bg-surface px-4 pb-4 pt-4">
          <Button onClick={handleEditProfile} size="lg" className="w-full">
            {i18n.t("user.directory.profile.editProfile")}
          </Button>
        </div>
      )}

      {/* Fixed Bottom Container - Error Banner + CTA stacked with flexbox */}
      {!isOwnProfile &&
        (mutationErrorMessage !== null || ctaProps !== null) && (
          <div
            className={
              "fixed inset-x-0 bottom-0 flex flex-col bg-surface border-t border-border " +
              (mutationErrorMessage !== null ? "pt-2" : "")
            }
          >
            {/* Mutation Error Banner - positioned above CTA */}
            {mutationErrorMessage !== null && (
              <div
                className="mx-4 mb-2 flex items-center gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive"
                data-testid="cta-error-banner"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1">{mutationErrorMessage}</span>
                <button
                  type="button"
                  onClick={clearMutationError}
                  className="-m-2 rounded p-3 hover:bg-destructive/10"
                  aria-label={i18n.t(
                    "user.directory.profile.requestIntroModal.close",
                  )}
                  data-testid="cta-error-banner-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* CTA - Fixed Bottom */}
            {ctaProps !== null && (
              <div data-testid="profile-cta" className="px-4 pb-4 pt-4">
                <CTAContent {...ctaProps} />
              </div>
            )}
          </div>
        )}

      {/* Request Intro Modal */}
      <RequestIntroModal
        isOpen={isRequestIntroModalOpen}
        onClose={closeRequestIntroModal}
        onSubmit={(message): void => {
          void handleSubmitIntroRequest(message);
        }}
        isSubmitting={isRequestingIntro}
        targetName={profile?.entry.displayName}
      />

      {/* Feature Not Implemented Modal - informational, non-error styling */}
      <FeatureNotImplementedModal
        isOpen={isFeatureNotImplementedModalOpen}
        onClose={closeFeatureNotImplementedModal}
      />
    </div>
  );
}
